import {
  getAllTrainingLogs,
  getTrainingLogsForProcedureRevision,
} from "../redux/trainingLogSlice";
import { dedupeTrainingLogsLatestPerTraineePerRevision } from "./trainingLogStatus";

const TRAINING_LOG_PART_FETCH_LIMIT = 10000;

function collectProcedureRevisionIdsForTrainingLogFetch(revs, routeRevisionId) {
  const fromList = Array.isArray(revs)
    ? revs
        .map((r) => r?.procedure_revision_id ?? r?.procedure_revision_ref)
        .filter(Boolean)
    : [];
  return Array.from(
    new Set([...fromList, routeRevisionId].filter(Boolean)),
  );
}

function loadTrainingLogsByPartRef(dispatch, partId) {
  return dispatch(
    getAllTrainingLogs({
      limit: TRAINING_LOG_PART_FETCH_LIMIT,
      offset: 0,
      part_ref: String(partId).trim(),
    }),
  )
    .unwrap()
    .then((payload) => {
      const raw = payload?.results ?? payload?.data;
      return Array.isArray(raw) ? raw : [];
    });
}

function loadTrainingLogsPerRevisionMerge(dispatch, partId, procedureRevisionIds) {
  const ids = Array.isArray(procedureRevisionIds)
    ? procedureRevisionIds.filter(Boolean).map(String)
    : [];
  if (!ids.length) {
    return Promise.resolve([]);
  }

  return Promise.all(
    ids.map((id) =>
      dispatch(
        getTrainingLogsForProcedureRevision({
          procedureRevisionId: id,
          limit: TRAINING_LOG_PART_FETCH_LIMIT,
          offset: 0,
          ...(partId ? { part_ref: String(partId).trim() } : {}),
        }),
      )
        .unwrap()
        .then((payload) => {
          const raw = payload?.results ?? payload?.data;
          const list = Array.isArray(raw) ? raw : [];
          return list.map((r) => ({
            ...r,
            procedure_revision_ref:
              r.procedure_revision_ref ?? r.procedure_revision_id ?? id,
          }));
        })
        .catch(() => []),
    ),
  ).then((chunks) => chunks.flat());
}

/**
 * Same training-log row set as Document.js (`useDocumentData`): part-scoped GET
 * merged with per-revision GET, then `dedupeTrainingLogsLatestPerTraineePerRevision`.
 * Use for PDF/ZIP so `buildTrainingLogDisplayRows` matches the on-screen table.
 */
export async function fetchMergedTrainingLogsForDocumentTable(
  dispatch,
  partId,
  revisionId,
  revisionListResults,
) {
  const revs = Array.isArray(revisionListResults) ? revisionListResults : [];
  const idsToLoad = collectProcedureRevisionIdsForTrainingLogFetch(
    revs,
    revisionId,
  );
  const ids = idsToLoad.length ? idsToLoad : [revisionId];

  if (!partId) {
    if (!ids.length) {
      return [];
    }
    const merged = await loadTrainingLogsPerRevisionMerge(
      dispatch,
      null,
      ids,
    );
    return dedupeTrainingLogsLatestPerTraineePerRevision(merged);
  }

  const [fromPart, fromRevs] = await Promise.all([
    loadTrainingLogsByPartRef(dispatch, partId).catch(() => []),
    ids.length
      ? loadTrainingLogsPerRevisionMerge(dispatch, partId, ids).catch(
          () => [],
        )
      : Promise.resolve([]),
  ]);
  const merged = [...fromPart, ...fromRevs];
  return dedupeTrainingLogsLatestPerTraineePerRevision(merged);
}
